import React from 'react'
import { useLocation } from 'react-router-dom'
import { Box, Tab, Tabs, Tooltip } from '@mui/material'
import chroma from 'chroma-js'

import Txt from '../components/Txt'
import Button from '../components/Button'
import IconButton from '../components/IconButton'
import MapContainer, { MapBounds } from '../components/MapContainer'
import CheckBox from '../components/CheckBox'
import SelectField from '../components/SelectField'
import importSettlementRestaurants from '../mutations/importSettlementRestaurants'
import setRestaurantSettlementIdea from '../mutations/setRestaurantSettlementIdea'
import { handleMutationError } from '../utils/errors'
import { RestaurantTourContent } from '../components/RestaurantTourDialog'
import LieferandoLinkButton, { getLieferandoSettlementUrl } from '../components/LieferandoLinkButton'
import { renderSettlementMessageDialog } from '../components/SettlementMessageDialog'
import {
	RestaurantDetail,
	RestaurantList,
	RankedRestaurant,
	ChainIcon,
	getRankedRestaurants,
} from '../components/RestaurantRanking'
import { APP_BAR_HEIGHT_REM } from '../components/AppBar'
import { usePxPerRemFactor, useWindowDimensions } from '../utils/hooks'
import { useCurrentUser } from '../utils/user'
import {
	Settlement,
	SettlementCandidate,
	SettlementRestaurant,
	RestaurantSettlementIdea,
	TourTarget,
	useRestaurantSettlementIdeas,
	useRestaurantTour,
	useSettlementCandidates,
	useTours,
	useSettlements,
} from '../utils/settlement'
import { LANDING_URL } from '../consts'

// Erhobene Länder; die Karte startet über der Landesmitte.
const STATES = [
	{ label: 'Brandenburg', value: 'Brandenburg', lat: 52.4, lon: 13.4 },
	{ label: 'Sachsen', value: 'Sachsen', lat: 51.0, lon: 13.3 },
	{ label: 'Sachsen-Anhalt', value: 'Sachsen-Anhalt', lat: 51.95, lon: 11.7 },
]

// Wie viele erreichbare Restaurants eine Haushaltsgröße voll versorgen; jedes deckt 1/n.
const FULL_SUPPLY_RESTAURANT_ITEMS = Array.from({ length: 10 }, (_, index) => ({
	label: String(index + 1),
	value: String(index + 1),
}))

// Einwohner in 5 Minuten Fahrzeit, ab denen ein Ort in der Liste steht.
const MIN_INHABITANTS = 1500

// Orte mit wenig Potenzial grau, damit sie auf der Karte zuruecktreten; relevante hellgruen.
// Gemessen am groessten Potenzial der Liste, als Wurzel, damit die Mitte nicht verblasst.
const LOW_POTENTIAL_COLOR = '#dadcd7'
const potentialScale = chroma.scale([LOW_POTENTIAL_COLOR, '#3fcb5c']).mode('lab')

// Orte mit Potenzial dreimal so groß wie die grauen, unabhängig von der Einwohnerzahl.
const MIN_POINT_SIZE = 3
const POTENTIAL_POINT_SIZE = MIN_POINT_SIZE * 3

// Das Potenzial ohne die 1-Personen-Haushalte: deren Werte werden von den Summen abgezogen.
const withoutSingles = (
	potential: NonNullable<Settlement['potential']>
): NonNullable<Settlement['potential']> => {
	const singles = potential.bySize.find(({ persons }) => persons === 1)
	return singles
		? {
				...potential,
				monthlyCustomers: potential.monthlyCustomers - singles.monthlyCustomers,
				monthlyHouseholds: potential.monthlyHouseholds - singles.monthlyHouseholds,
				monthlyLostRevenue: potential.monthlyLostRevenue - singles.monthlyLostRevenue,
				monthlyGroupOrders: potential.monthlyGroupOrders - singles.monthlyGroupOrders,
				monthlyPlatformRevenue: potential.monthlyPlatformRevenue - singles.monthlyPlatformRevenue,
				bySize: potential.bySize.filter(({ persons }) => persons !== 1),
		  }
		: potential
}

const getPotential = (settlement: Settlement): number | null =>
	settlement.potential?.monthlyPlatformRevenue ?? null

const getPotentialColor = (potential: number | null, maxPotential: number): string =>
	potential === null || !maxPotential
		? LOW_POTENTIAL_COLOR
		: potentialScale(Math.sqrt(Math.max(0, Math.min(1, potential / maxPotential)))).hex()

const getPointSize = (potential: number | null, maxPotential: number): number =>
	potential === null || !maxPotential ? MIN_POINT_SIZE : POTENTIAL_POINT_SIZE

const formatNumber = (value: number | null | undefined): string =>
	value === null || value === undefined ? '—' : Math.round(value).toLocaleString('de-DE')

const formatEuro = (value: number | null | undefined): string =>
	value === null || value === undefined ? '—' : `${Number(value).toLocaleString('de-DE')} €`

const sxPanel = {
	position: 'absolute',
	left: '1rem',
	top: '1rem',
	zIndex: 1000,
	width: '22rem',
	maxHeight: 'calc(100% - 2rem)',
	overflowY: 'auto',
	bgcolor: 'white',
	boxShadow: 2,
	borderRadius: '0.5rem',
	p: '1rem',
} as const

const sxRow = {
	'display': 'flex',
	'alignItems': 'center',
	'gap': '0.5rem',
	'p': '0.25rem 0.5rem',
	'borderRadius': '0.25rem',
	'cursor': 'pointer',
	'&:hover': { bgcolor: 'grey.100' },
} as const

const sxRowSelected = {
	...sxRow,
	'bgcolor': 'grey.200',
	'&:hover': { bgcolor: 'grey.200' },
} as const

const sxDot = {
	width: '0.75rem',
	height: '0.75rem',
	borderRadius: '50%',
	flexShrink: 0,
} as const

const sxHeaderRow = {
	display: 'flex',
	alignItems: 'center',
	gap: '0.5rem',
	p: '0 0.5rem',
} as const

const slotPropsTooltip = {
	tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: '20rem' } },
} as const

const sxMetric = { cursor: 'help' } as const

// Was zum angeklickten Balken gehoert, steht in einem eigenen Rahmen — sonst haengt es lose unter
// dem Diagramm und man sieht nicht, dass es die Auswahl ist.
const sxSelection = {
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
	p: '0.6rem 0.75rem',
	m: '0.5rem 0 0 0',
} as const

const POTENTIAL_COLUMN_WIDTH = '4.5rem'

const SettlementRow = React.memo(
	({
		settlement,
		maxPotential,
		selected,
		onSelect,
	}: {
		settlement: Settlement
		maxPotential: number
		selected: boolean
		onSelect: (id: string) => void
	}): JSX.Element => {
		const rowRef = React.useRef<HTMLDivElement>(null)
		const handleClick = React.useCallback(() => onSelect(settlement.id), [onSelect, settlement.id])

		// Beim Blättern per Pfeiltaste wandert die Auswahl sonst aus dem sichtbaren Bereich.
		React.useEffect(() => {
			if (selected) rowRef.current?.scrollIntoView({ block: 'nearest' })
		}, [selected])

		return (
			<Box ref={rowRef} sx={selected ? sxRowSelected : sxRow} onClick={handleClick}>
				<Box sx={sxDot} bgcolor={getPotentialColor(getPotential(settlement), maxPotential)} />
				<Box flex="1" minWidth="0">
					<Txt variant="body2" noWrap title={settlement.name}>
						{settlement.name}
						{settlement.inPortfolio ? ' ·' : ''}
					</Txt>
				</Box>
				<Txt variant="caption" color="text.secondary" width="0.75rem" textAlign="right">
					{settlement.deliveryCheckedOn === null ? '?' : ''}
				</Txt>
				<Txt variant="body2" width={POTENTIAL_COLUMN_WIDTH} textAlign="right">
					{formatEuro(getPotential(settlement))}
				</Txt>
			</Box>
		)
	}
)
SettlementRow.displayName = 'SettlementRow'

const sxDivider = { borderTop: '1px solid', borderColor: 'divider', m: '0.75rem 0' } as const

const formatRating = (value: number): string => value.toFixed(1).replace('.', ',')

// Feste Pixelmaße statt width:100% — so bleibt die Schrift bei 1:1 lesbar und das Diagramm
// nimmt nicht die volle Panelbreite ein.
const CHART_WIDTH = 216
const CHART_HEIGHT = 78
const PLOT_TOP = 4
const PLOT_BOTTOM = 58
const PLOT_LEFT = 18
const CHART_FONT_SIZE = 10
const BAR_WIDTH = 18
const BAR_RADIUS = 4
const CHART_AXIS_COLOR = '#e4e2dd'
const CHART_TEXT_COLOR = '#6b6a66'
const CHART_TEXT_SELECTED_COLOR = '#2f2e2b'
const CHART_BAR_COLOR = '#b3781f'
const CHART_BAR_MUTED_COLOR = '#dbc096'

const sxChart = { display: 'block', m: '0.25rem 0' } as const

type ChartEntry = { label: string; count: number; tooltip: string }

type ChartBucket = ChartEntry & {
	x: number
	y: number
	height: number
	centerX: number
	bandWidth: number
}

// Achsenschritt auf eine runde Zahl bringen. Kleine Zählungen wie das MBW-Histogramm behalten
// ceil(max/3) — bei Einwohnerzahlen ergäbe das krumme Beschriftungen wie 31.583.
const getTickStep = (maxCount: number): number => {
	const raw = Math.max(1, Math.ceil(maxCount / 3))
	if (raw <= 10) return raw
	const magnitude = 10 ** Math.floor(Math.log10(raw))
	return [1, 2, 5, 10].map((factor) => factor * magnitude).find((step) => step >= raw) ?? raw
}

// Achse und Geometrie teilen sich das MBW-Histogramm, die Bundesland-Verteilung und die
// Fahrzeit-Einwohner. `plotLeft` gibt der Achse mehr Luft, wenn die Beschriftung breiter wird.
const buildChart = (entries: ChartEntry[], plotLeft: number = PLOT_LEFT) => {
	const maxCount = Math.max(...entries.map((entry) => entry.count))
	const tickStep = getTickStep(maxCount)
	const tickMax = tickStep * Math.max(1, Math.ceil(maxCount / tickStep))
	const bandWidth = (CHART_WIDTH - plotLeft) / entries.length

	return {
		plotLeft,
		buckets: entries.map((entry, index) => {
			const height = ((PLOT_BOTTOM - PLOT_TOP) * entry.count) / tickMax
			return {
				...entry,
				x: plotLeft + bandWidth * (index + 0.5) - BAR_WIDTH / 2,
				y: PLOT_BOTTOM - height,
				height,
				centerX: plotLeft + bandWidth * (index + 0.5),
				bandWidth,
			}
		}),
		ticks: Array.from({ length: tickMax / tickStep + 1 }, (_unused, index) => {
			const value = index * tickStep
			return { value, y: PLOT_BOTTOM - ((PLOT_BOTTOM - PLOT_TOP) * value) / tickMax }
		}),
		isEmpty: maxCount === 0,
	}
}

// Säule mit abgerundeter Datenkante oben und eckigem Fuß auf der Grundlinie.
const getBarPath = (x: number, y: number, height: number): string => {
	const radius = Math.min(BAR_RADIUS, height / 2)
	const right = x + BAR_WIDTH
	return `M${x},${PLOT_BOTTOM} L${x},${y + radius} Q${x},${y} ${x + radius},${y} L${
		right - radius
	},${y} Q${right},${y} ${right},${y + radius} L${right},${PLOT_BOTTOM} Z`
}

const ChartBar = React.memo(
	({
		bucket,
		selected,
		onSelect,
	}: {
		bucket: ChartBucket
		selected: boolean
		onSelect: (label: string) => void
	}): JSX.Element => {
		const handleClick = React.useCallback(() => onSelect(bucket.label), [bucket.label, onSelect])
		const selectable = bucket.count > 0

		return (
			<g onClick={selectable ? handleClick : undefined} cursor={selectable ? 'pointer' : 'default'}>
				<title>{bucket.tooltip}</title>
				<rect
					x={bucket.centerX - bucket.bandWidth / 2}
					y={PLOT_TOP}
					width={bucket.bandWidth}
					height={CHART_HEIGHT - PLOT_TOP}
					fill="transparent"
				/>
				{bucket.height ? (
					<path
						d={getBarPath(bucket.x, bucket.y, bucket.height)}
						fill={selected ? CHART_BAR_COLOR : CHART_BAR_MUTED_COLOR}
					/>
				) : null}
				<text
					x={bucket.centerX}
					y={CHART_HEIGHT - 3}
					textAnchor="middle"
					fontSize={CHART_FONT_SIZE}
					fontWeight={selected ? 700 : 400}
					fill={selected ? CHART_TEXT_SELECTED_COLOR : CHART_TEXT_COLOR}
				>
					{bucket.label}
				</text>
			</g>
		)
	}
)
ChartBar.displayName = 'ChartBar'

const BarChart = React.memo(
	({
		chart,
		selectedLabel,
		onSelect,
		ariaLabel,
		formatTick,
	}: {
		chart: ReturnType<typeof buildChart>
		selectedLabel: string | undefined
		onSelect: (label: string) => void
		ariaLabel: string
		formatTick?: (value: number) => string
	}): JSX.Element => (
		<Box
			component="svg"
			sx={sxChart}
			width={CHART_WIDTH}
			height={CHART_HEIGHT}
			viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
			role="img"
			aria-label={ariaLabel}
		>
			{chart.ticks.map((tick) => (
				<g key={tick.value}>
					<line
						x1={chart.plotLeft}
						y1={tick.y}
						x2={CHART_WIDTH}
						y2={tick.y}
						stroke={CHART_AXIS_COLOR}
						strokeWidth={1}
					/>
					<text
						x={chart.plotLeft - 5}
						y={tick.y + 3.5}
						textAnchor="end"
						fontSize={CHART_FONT_SIZE}
						fill={CHART_TEXT_COLOR}
					>
						{formatTick ? formatTick(tick.value) : tick.value}
					</text>
				</g>
			))}
			{chart.buckets.map((bucket) => (
				<ChartBar
					key={bucket.label}
					bucket={bucket}
					selected={bucket.label === selectedLabel}
					onSelect={onSelect}
				/>
			))}
		</Box>
	)
)
BarChart.displayName = 'BarChart'

// Das Liefergebiet, mit dem auch Potenzial und Bericht rechnen.
const DELIVERY_AREA_MINUTES = 5

// Durchschnittsalter Deutschland aus denselben Zensus-2022-Gittern, nach Einwohnern gewichtet —
// so ist der Vergleich mit den Siedlungswerten methodisch gleich.
const GERMANY_AGE_AVG = 44.2

const METRIC_AGE_DESCRIPTION =
	'Zensus 2022, 100-m-Gitter, innerhalb der 5-Minuten-Isochrone.\n\n' +
	'Bestellquote: Anteil, der mindestens einmal im Monat Essen liefern lässt, nach Altersklassen ' +
	'gewichtet (unter 18: 38 %, 18–29: 49 %, 30–49: 38 %, 50–64: 25 %, 65+: 12 %). Gemessen sind ' +
	'nur 18–29 und der Schnitt von 31 % (GfK/Mastercard 2022), der Rest ist gesetzt. Der Flyer ' +
	'rechnet mit dieser Quote.'

const sxDetailHeader = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'flex-start',
	gap: '0.75rem',
} as const

const sxAgeBadge = { cursor: 'help', textAlign: 'right', flexShrink: 0 } as const

const formatOneDecimal = (value: number): string =>
	value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

// Nur der Abstand zum Bundesschnitt steht im Kopf; die Altersklassen zeigt der Tooltip.
const AgeBadge = React.memo(
	({ settlement, ageAvg }: { settlement: Settlement; ageAvg: number }): JSX.Element => {
		const tooltip = React.useMemo(() => {
			const total = settlement.ageGroups.reduce((sum, group) => sum + group.inhabitants, 0)
			return [
				`⌀ ${formatOneDecimal(ageAvg)} Jahre (Deutschland ${formatOneDecimal(GERMANY_AGE_AVG)})`,
				'',
				...settlement.ageGroups.map(
					(group) => `${group.label}: ${Math.round((group.inhabitants / total) * 100)} %`
				),
				'',
				`Bestellquote: ${
					settlement.orderRate === null ? '—' : `${Math.round(settlement.orderRate * 100)} %`
				}`,
				'',
				METRIC_AGE_DESCRIPTION,
			].join('\n')
		}, [ageAvg, settlement.ageGroups, settlement.orderRate])

		const difference = ageAvg - GERMANY_AGE_AVG

		return (
			<Tooltip title={tooltip} slotProps={slotPropsTooltip} arrow>
				<Box sx={sxAgeBadge}>
					<Txt variant="h6">
						{difference < 0 ? '−' : '+'}
						{formatOneDecimal(Math.abs(difference))} J.
					</Txt>
					<Txt variant="caption" color="text.secondary">
						{difference < 0 ? 'jünger' : 'älter'} als ⌀ DE
					</Txt>
				</Box>
			</Tooltip>
		)
	}
)
AgeBadge.displayName = 'AgeBadge'

const sxProviderRow = {
	'display': 'flex',
	'gap': '0.5rem',
	'p': '0.15rem 0',
	'cursor': 'pointer',
	'&:hover': { bgcolor: 'grey.100' },
} as const

// Ausgegraut: eine einzelne Lieferando-Bestellung zum MBW bringt dem Restaurant Verlust.
const sxUnprofitableProviderRow = { ...sxProviderRow, opacity: 0.4 } as const

// Grün hinterlegt: liegt im eigenen 5-min-Gebiet; der negative Rand hält die Spalten bündig.
const sxLocalProviderRow = {
	...sxProviderRow,
	'bgcolor': '#e3f2e5',
	'p': '0.15rem 0.35rem',
	'm': '0 -0.35rem',
	'borderRadius': '0.25rem',
	'&:hover': { bgcolor: '#d0ead3' },
} as const

const MARGIN_TOOLTIP =
	'Deckungsbeitrag einer einzelnen, selbst ausgefahrenen Lieferando-Bestellung zum MBW, wie ' +
	'die Spalte „Heute“ der Tourrechnung: 30 % Wareneinsatz, 14 % Provision, Fahrer 17,50 €/h ' +
	'und 0,30 €/km für Hin- und Rückweg laut Mapbox.'

// Farbkreis je Küchenkategorie; grau, solange keine Küchen erhoben sind oder keine Kategorie passt.
const CUISINE_CATEGORIES = {
	standard: { color: '#4a7fd6', label: 'Standard (Burger, Pizza, Döner …)' },
	indian: { color: '#ed8b2f', label: 'Indisch' },
	asian: { color: '#3fa55a', label: 'Asiatisch' },
	greek: { color: '#2aa8b8', label: 'Griechisch' },
	steak: { color: '#d64545', label: 'Steak' },
	oriental: { color: '#8e5cc4', label: 'Orientalisch' },
	sweet: { color: '#e573a6', label: 'Süßes' },
} as const
const OTHER_CUISINE_COLOR = '#c9c7c2'

const sxCuisineDot = {
	width: '0.55rem',
	height: '0.55rem',
	borderRadius: '50%',
	flexShrink: 0,
	alignSelf: 'center',
} as const

const CuisineDot = React.memo(
	({
		cuisineCategory,
		cuisines,
	}: {
		cuisineCategory: SettlementRestaurant['cuisineCategory']
		cuisines: string[] | null
	}): JSX.Element => (
		<Box
			sx={sxCuisineDot}
			bgcolor={cuisineCategory ? CUISINE_CATEGORIES[cuisineCategory].color : OTHER_CUISINE_COLOR}
			title={
				cuisineCategory
					? CUISINE_CATEGORIES[cuisineCategory].label
					: cuisines
					? 'Sonstige Küche'
					: 'Küchen nicht erhoben'
			}
		/>
	)
)
CuisineDot.displayName = 'CuisineDot'

// Welches Restaurant seine Strecke in die Gemeinde auf der Karte zeigt, Anbieter wie Kandidat der
// Potenzialanalyse; die Zeilen liegen tief in der Gemeinde-Karte, deshalb als Context statt als Props.
const TourSelectionContext = React.createContext<{
	selectedSlug: string | undefined
	onSelectSlug: (slug: string) => void
}>({
	selectedSlug: undefined,
	onSelectSlug: () => undefined,
})

// Klick auf einen Anbieter zeigt seine Strecke in diesen Ort, ein zweiter blendet sie aus.
const ProviderRow = React.memo(
	({ provider }: { provider: SettlementRestaurant }): JSX.Element => {
		const { selectedSlug, onSelectSlug } = React.useContext(TourSelectionContext)
		const handleClick = React.useCallback(
			() => onSelectSlug(provider.slug),
			[onSelectSlug, provider.slug]
		)

		return (
			<Box
				sx={
					provider.isInDeliveryArea
						? sxLocalProviderRow
						: provider.singleOrderMarginShare !== null && provider.singleOrderMarginShare < 0
						? sxUnprofitableProviderRow
						: sxProviderRow
				}
				onClick={handleClick}
			>
				<CuisineDot cuisineCategory={provider.cuisineCategory} cuisines={provider.cuisines} />
				<Box flex="1" minWidth="0">
					<Txt
						variant="body2"
						noWrap
						title={
							provider.cuisines?.length
								? `${provider.name}\n${provider.cuisines.join(' · ')}`
								: provider.name
						}
						fontWeight={provider.slug === selectedSlug ? 'bold' : undefined}
					>
						{provider.name}
					</Txt>
				</Box>
				{provider.isChain ? <ChainIcon /> : null}
				<Txt variant="caption" color="text.secondary" width="2.4rem" textAlign="right">
					{provider.rating === null ? '—' : `${formatRating(provider.rating)} ★`}
				</Txt>
				<Txt
					variant="caption"
					color="text.secondary"
					width="3rem"
					textAlign="right"
					title={`${provider.driveDistanceKm ?? '—'} km · ${
						provider.driveMinutes ?? '—'
					} Min. einfache Fahrt\n\n${MARGIN_TOOLTIP}`}
				>
					{provider.singleOrderMarginShare === null
						? '—'
						: `${Math.round(provider.singleOrderMarginShare * 100)} %`}
				</Txt>
				<Txt variant="body2" width="3.5rem" textAlign="right">
					{formatEuro(provider.minOrderValue)}
				</Txt>
			</Box>
		)
	}
)
ProviderRow.displayName = 'ProviderRow'

const sxCandidateRow = {
	'display': 'flex',
	'gap': '0.5rem',
	'p': '0.2rem 0',
	'cursor': 'pointer',
	'&:hover': { bgcolor: 'grey.100' },
} as const
const sxUnprofitableCandidateRow = { ...sxCandidateRow, opacity: 0.4 } as const

const formatPercentOrDash = (share: number | null): string =>
	share === null ? '—' : `${Math.round(share * 100)} %`

const formatKm = (km: number | null): string =>
	km === null ? '—' : km.toLocaleString('de-DE', { maximumFractionDigits: 1 })

// Die Einzelfahrt hierher Zeile für Zeile wie in der Tourrechnung, für den Tooltip am MBW.
const getCandidateCalculation = (candidate: SettlementCandidate): string[] =>
	candidate.singleTour
		? [
				`Mindestbestellwert: ${formatEuroCents(candidate.singleTour.orderValue)}`,
				`+ Liefergebühr: ${formatEuroCents(candidate.singleTour.deliveryFee)}`,
				`− Wareneinsatz (30 %): ${formatEuroCents(candidate.singleTour.foodCost)}`,
				`− Provision (14 %): ${formatEuroCents(candidate.singleTour.commission)}`,
				`− Fahrtkosten (${formatKm(
					candidate.driveDistanceKm === null ? null : 2 * candidate.driveDistanceKm
				)} km, ${Math.round(candidate.singleTour.minutes)} min hin und zurück): ${formatEuroCents(
					candidate.singleTour.driveCost
				)}`,
				`= bleiben je Fahrt: ${formatEuroCents(candidate.singleTour.profit)} (${formatPercentOrDash(
					candidate.singleOrderMarginShare
				)} DB)`,
		  ]
		: ['Keine Strecke berechnet.']

// Ein Restaurant im Umkreis, das heute nicht in den Ort liefert, mit seiner Rechnung für die Fahrt
// hierher; ein Klick zeigt die Strecke auf der Karte.
const CandidateRow = React.memo(({ candidate }: { candidate: SettlementCandidate }): JSX.Element => {
	const { selectedSlug, onSelectSlug } = React.useContext(TourSelectionContext)
	const handleClick = React.useCallback(
		() => onSelectSlug(candidate.slug),
		[candidate.slug, onSelectSlug]
	)

	return (
		<Box
			sx={
				candidate.singleOrderMarginShare !== null && candidate.singleOrderMarginShare < 0
					? sxUnprofitableCandidateRow
					: sxCandidateRow
			}
			onClick={handleClick}
		>
			<CuisineDot cuisineCategory={candidate.cuisineCategory} cuisines={candidate.cuisines} />
			<Box flex="1" minWidth="0">
				<Txt
					variant="body2"
					noWrap
					fontWeight={candidate.slug === selectedSlug ? 'bold' : undefined}
					title={[
						candidate.name,
						candidate.addressCity ?? '',
						candidate.cuisines?.join(' · ') ?? '',
						`hierher ${formatKm(candidate.driveDistanceKm ?? candidate.straightKm)} km · ${
							candidate.driveMinutes ?? '—'
						} min`,
						`heute bis ${formatKm(candidate.farthestDriveDistanceKm)} km · ${
							candidate.farthestDriveMinutes ?? '—'
						} min (${candidate.farthestSettlementName})`,
					]
						.filter(Boolean)
						.join('\n')}
				>
					{candidate.name}
				</Txt>
			</Box>
			<Txt variant="caption" color="text.secondary" width="2.4rem" textAlign="right">
				{candidate.rating === null ? '—' : `${formatRating(candidate.rating)} ★`}
			</Txt>
			<Txt
				variant="caption"
				color="text.secondary"
				width="3rem"
				textAlign="right"
				title={`${
					candidate.singleOrderProfit === null
						? '—'
						: formatEuroCents(Math.round(candidate.singleOrderProfit * 100) / 100)
				} bleiben je Einzelfahrt\n\n${MARGIN_TOOLTIP}`}
			>
				{candidate.singleOrderMarginShare === null
					? '—'
					: `${Math.round(candidate.singleOrderMarginShare * 100)} %`}
			</Txt>
			<Txt
				variant="body2"
				width="3.5rem"
				textAlign="right"
				title={[
					`Hochgerechnet auf 30 % Deckungsbeitrag, Fahrt hierher ${formatKm(
						candidate.driveDistanceKm
					)} km · ${candidate.driveMinutes ?? '—'} min:`,
					...getCandidateCalculation(candidate),
					'',
					`Heute im Schnitt ${formatPercentOrDash(candidate.averageMarginShare)} DB:`,
					...candidate.servedSettlements.map(
						({ settlementName, minOrderValue, marginShare }) =>
							`${settlementName}: ${formatEuro(minOrderValue)} MBW · ${formatPercentOrDash(
								marginShare
							)} DB`
					),
				].join('\n')}
			>
				{formatEuro(candidate.minOrderValue)}
			</Txt>
		</Box>
	)
})
CandidateRow.displayName = 'CandidateRow'

// Potenzialanalyse auf Knopfdruck: Restaurants im Umkreis, die noch nicht liefern, und ob sich die
// Fahrt hierher für sie lohnen würde. Der erste Abruf holt die Strecken bei Mapbox.
const SettlementCandidates = React.memo(
	({ settlementId }: { settlementId: string }): JSX.Element => {
		const [requested, setRequested] = React.useState(false)
		const handleRequest = React.useCallback(() => setRequested(true), [])
		const { candidates, loading } = useSettlementCandidates(requested ? settlementId : undefined)
		const [reachesOnly, setReachesOnly] = React.useState(true)
		const visibleCandidates = React.useMemo(
			() => (reachesOnly ? candidates.filter(({ reachesAlready }) => reachesAlready) : candidates),
			[candidates, reachesOnly]
		)

		return !requested ? (
			<Button variant="secondButton" onClick={handleRequest} m="0.75rem 0 0 0">
				Weitere Restaurants im Umkreis
			</Button>
		) : loading ? (
			<Txt variant="body2" color="text.secondary" m="0.75rem 0 0 0">
				Strecken werden berechnet …
			</Txt>
		) : (
			<Box sx={sxSelection}>
				<Txt variant="caption" color="text.secondary" display="block">
					Liefern noch nicht hierher, bis 20 km Luftlinie · MBW hochgerechnet, niedrigster zuerst
				</Txt>
				<CheckBox
					label="Nur wer heute schon so weit fährt"
					defaultValue={reachesOnly}
					onChange={setReachesOnly}
					small
				/>
				{visibleCandidates.length ? (
					visibleCandidates.map((candidate) => (
						<CandidateRow key={candidate.slug} candidate={candidate} />
					))
				) : (
					<Txt variant="caption" color="text.secondary">
						Keine weiteren Restaurants im Umkreis.
					</Txt>
				)}
			</Box>
		)
	}
)
SettlementCandidates.displayName = 'SettlementCandidates'

const sxRemoteProvidersSpacer = { height: '0.5rem' } as const

// Mehrere Marken an einer Adresse, meist eine Küche: eine Klammer links verbindet ihre Farbkreise,
// von der Mitte der ersten bis zur Mitte der letzten Zeile, ohne die Zeilen zu verschieben.
const sxProviderGroup = { position: 'relative' } as const

const sxProviderGroupBracket = {
	position: 'absolute',
	left: '-0.55rem',
	top: '0.8rem',
	bottom: '0.8rem',
	width: '0.4rem',
	border: '1.5px solid',
	borderRight: 'none',
	borderColor: 'grey.400',
	borderRadius: '0.35rem 0 0 0.35rem',
	pointerEvents: 'none',
} as const

// Restaurants an derselben Adresse stehen beieinander, an der Stelle des besten von ihnen. Ohne
// Adresse bleibt jedes für sich; der Präfix verhindert, dass JS Zahlen-Schlüssel nach vorn sortiert.
const groupByAddress = (providers: SettlementRestaurant[]): SettlementRestaurant[][] =>
	Object.values(
		providers.reduce<Record<string, SettlementRestaurant[]>>((groups, provider) => {
			const key = provider.addressLine
				? `address:${provider.addressLine}|${provider.addressZipCode}`.toLowerCase()
				: `id:${provider.id}`
			return { ...groups, [key]: [...(groups[key] ?? []), provider] }
		}, {})
	)

const ProviderGroup = React.memo(
	({ providers }: { providers: SettlementRestaurant[] }): JSX.Element =>
		providers.length > 1 ? (
			<Box
				sx={sxProviderGroup}
				title={`${providers.length} Restaurants an derselben Adresse: ${
					providers[0].addressLine
				}, ${providers[0].addressZipCode ?? ''} ${providers[0].addressCity ?? ''}`}
			>
				<Box sx={sxProviderGroupBracket} />
				{providers.map((provider) => (
					<ProviderRow key={provider.id} provider={provider} />
				))}
			</Box>
		) : (
			<ProviderRow provider={providers[0]} />
		)
)
ProviderGroup.displayName = 'ProviderGroup'

// Alle liefernden Anbieter mit Küchen und Bewertung; klappt am Info-Symbol des Punktdiagramms auf.
const ProviderDetails = React.memo(
	({ providers }: { providers: SettlementRestaurant[] }): JSX.Element => {
		// Nach Deckungsbeitrag absteigend; ohne Fahrzeit unbekannt und ans Ende.
		const sortedProviders = React.useMemo(
			() =>
				[...providers].sort(
					(a, b) =>
						(b.singleOrderMarginShare ?? -Infinity) - (a.singleOrderMarginShare ?? -Infinity) ||
						(a.minOrderValue ?? 0) - (b.minOrderValue ?? 0)
				),
			[providers]
		)
		// Restaurants im eigenen 5-min-Gebiet sind für Mitbestellt irrelevant und stehen nur zur Info oben.
		const remoteGroups = React.useMemo(
			() => groupByAddress(sortedProviders.filter((provider) => !provider.isInDeliveryArea)),
			[sortedProviders]
		)
		const localGroups = React.useMemo(
			() => groupByAddress(sortedProviders.filter((provider) => provider.isInDeliveryArea)),
			[sortedProviders]
		)

		return (
			<Box sx={sxSelection}>
				{localGroups.length ? (
					<>
						{localGroups.map((group) => (
							<ProviderGroup key={group[0].id} providers={group} />
						))}
						<Box sx={sxRemoteProvidersSpacer} />
					</>
				) : null}
				{remoteGroups.length ? (
					remoteGroups.map((group) => <ProviderGroup key={group[0].id} providers={group} />)
				) : (
					<Txt variant="caption" color="text.secondary">
						Alle Anbieter liegen im eigenen 5-min-Gebiet.
					</Txt>
				)}
			</Box>
		)
	}
)
ProviderDetails.displayName = 'ProviderDetails'

// Wird ein Ort ohne Erhebung ausgewählt, startet der Import von selbst. Die Verzögerung ist
// nötig, weil die Pfeiltasten die Auswahl schnell durch die Liste schieben — ohne sie würde
// jeder Zwischenschritt einen Lieferando-Abruf auslösen.
const AUTO_IMPORT_DELAY_MS = 1200

// Modulweit, damit ein Ort nach einem Fehlschlag nicht bei jeder erneuten Auswahl wieder losläuft
// (in Produktion fehlt Playwright, dort schlägt jeder Versuch fehl).
const attemptedImports = new Set<string>()

const ImportDelivery = React.memo(({ settlementId }: { settlementId: string }): JSX.Element => {
	const [running, setRunning] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)
	const [pending, setPending] = React.useState(!attemptedImports.has(settlementId))

	const runImport = React.useCallback(async () => {
		attemptedImports.add(settlementId)
		setPending(false)
		setError(null)
		setRunning(true)
		const result = await importSettlementRestaurants({ settlementId })
		setRunning(false)
		if (result.errors) {
			setError(
				typeof result.errors === 'string'
					? result.errors
					: result.errors?.[0]?.message ?? 'Import fehlgeschlagen'
			)
		}
	}, [settlementId])

	React.useEffect(() => {
		if (!pending) return
		const timeout = setTimeout(runImport, AUTO_IMPORT_DELAY_MS)
		return () => clearTimeout(timeout)
	}, [pending, runImport])

	return running || pending ? (
		<Txt variant="body2" color="text.secondary">
			Wird von Lieferando geholt …
		</Txt>
	) : (
		<>
			{error ? (
				<Txt variant="body2" color="error.main">
					{error}
				</Txt>
			) : null}
			<Button variant="mainButton" onClick={runImport} m="0.75rem 0 0 0">
				Erneut versuchen
			</Button>
		</>
	)
})
ImportDelivery.displayName = 'ImportDelivery'

// Zufällige Pause zwischen zwei Abrufen, damit die Anfragen nicht im Takt bei Lieferando ankommen.
// Zwei Fehlschläge in Folge deuten auf Cloudflare hin; dann bricht der Lauf ab statt weiterzubohren.
const BULK_IMPORT_MIN_DELAY_MS = 5000
const BULK_IMPORT_MAX_DELAY_MS = 15000
const BULK_IMPORT_MAX_FAILURES_IN_A_ROW = 2

// Das Potenzial aller angezeigten Orte zusammen, je Haushaltsgröße; für den Tooltip an der Spalte.
const getPotentialTotalsText = (settlements: Settlement[]): string => {
	const sizes = settlements
		.flatMap((settlement) => settlement.potential?.bySize ?? [])
		.reduce<Record<number, { customers: number; revenue: number }>>(
			(totals, size) => ({
				...totals,
				[size.persons]: {
					customers: (totals[size.persons]?.customers ?? 0) + size.monthlyCustomers,
					revenue: (totals[size.persons]?.revenue ?? 0) + size.monthlyPlatformRevenue,
				},
			}),
			{}
		)
	const persons = Object.keys(sizes).map(Number)
	const maxPersons = Math.max(...persons)
	return [
		`Alle ${settlements.length} Orte zusammen, im Monat:`,
		...persons.map(
			(size) =>
				`${size === maxPersons ? `${size}+` : size} Pers.: ${formatNumber(
					sizes[size].customers
				)} Besteller · ${formatEuro(sizes[size].revenue)} für Mitbestellt`
		),
		`Gesamt: ${formatNumber(
			persons.reduce((sum, size) => sum + sizes[size].customers, 0)
		)} Besteller · ${formatEuro(
			persons.reduce((sum, size) => sum + sizes[size].revenue, 0)
		)} für Mitbestellt`,
	].join('\n')
}

const BulkImportRestaurants = React.memo(
	({ settlements }: { settlements: Settlement[] }): JSX.Element | null => {
		const [status, setStatus] = React.useState<string | null>(null)
		const [running, setRunning] = React.useState(false)
		const stopRequested = React.useRef(false)

		// Kleine Orte zuerst: in Großstädten liefert ohnehin jemand günstig, dort ist am wenigsten zu holen.
		const uncheckedSettlements = React.useMemo(
			() =>
				settlements
					.filter((settlement) => settlement.deliveryCheckedOn === null && settlement.zipCode)
					.sort((a, b) => (a.inhabitants5min ?? 0) - (b.inhabitants5min ?? 0)),
			[settlements]
		)

		React.useEffect(
			() => () => {
				stopRequested.current = true
			},
			[]
		)

		const handleStart = React.useCallback(async () => {
			stopRequested.current = false
			setRunning(true)
			let failuresInARow = 0
			let imported = 0
			for (const [index, settlement] of uncheckedSettlements.entries()) {
				if (stopRequested.current || failuresInARow >= BULK_IMPORT_MAX_FAILURES_IN_A_ROW) break
				setStatus(`${index + 1} von ${uncheckedSettlements.length}: ${settlement.name} …`)
				// eslint-disable-next-line no-await-in-loop
				const result = await importSettlementRestaurants({ settlementId: settlement.id })
				if (result.errors) {
					failuresInARow += 1
				} else {
					failuresInARow = 0
					imported += 1
				}
				if (index === uncheckedSettlements.length - 1) break
				const delayMs =
					BULK_IMPORT_MIN_DELAY_MS +
					Math.random() * (BULK_IMPORT_MAX_DELAY_MS - BULK_IMPORT_MIN_DELAY_MS)
				setStatus(
					`${index + 1} von ${uncheckedSettlements.length} · nächster Abruf in ${Math.round(
						delayMs / 1000
					)} s`
				)
				// eslint-disable-next-line no-await-in-loop
				await new Promise((resolve) => setTimeout(resolve, delayMs))
			}
			setRunning(false)
			setStatus(
				failuresInARow >= BULK_IMPORT_MAX_FAILURES_IN_A_ROW
					? `Abgebrochen nach ${imported} Orten: ${BULK_IMPORT_MAX_FAILURES_IN_A_ROW} Fehlschläge in Folge (Cloudflare?)`
					: `${imported} Orte abgerufen`
			)
		}, [uncheckedSettlements])

		const handleStop = React.useCallback(() => {
			stopRequested.current = true
			setStatus('Wird nach dem laufenden Abruf gestoppt …')
		}, [])

		return running || uncheckedSettlements.length ? (
			<Box m="0 0 0.75rem 0">
				{running ? (
					<Button variant="secondButton" onClick={handleStop}>
						Abruf stoppen
					</Button>
				) : (
					<Button variant="mainButton" onClick={handleStart}>
						{`${uncheckedSettlements.length} Orte von Lieferando abrufen`}
					</Button>
				)}
				{status ? (
					<Txt variant="caption" color="text.secondary" m="0.25rem 0 0 0">
						{status}
					</Txt>
				) : null}
			</Box>
		) : null
	}
)
BulkImportRestaurants.displayName = 'BulkImportRestaurants'

// Öffnet den Briefdialog mit Restaurantauswahl und Vorschau.
const SettlementLetterButton = React.memo(
	({ settlement }: { settlement: Settlement }): JSX.Element => {
		const handleClick = React.useCallback(
			() => renderSettlementMessageDialog(settlement, 'PAPER'),
			[settlement]
		)

		return (
			<IconButton
				iconName="MarkunreadMailboxOutlined"
				toolTip="Briefe (A4) ansehen"
				onClick={handleClick}
			/>
		)
	}
)
SettlementLetterButton.displayName = 'SettlementLetterButton'

const sxSettlementTitle = { display: 'flex', alignItems: 'center', gap: '0.5rem' } as const

const formatPercent = (value: number): string => `${Math.round(value * 100)} %`

// Dieselbe Markenfarbe wie im Bundesland-Bericht, für den Mitbestellt-Umsatz.
const BRAND_COLOR = '#7a2350'

// Die Rechnung als Trichter: links die Zahl, rechts woraus sie entsteht.
const sxFunnel = {
	display: 'grid',
	gridTemplateColumns: 'auto 1fr',
	columnGap: '0.6rem',
	rowGap: '0.2rem',
	alignItems: 'baseline',
	m: '0.4rem 0 0 0',
} as const

// Zahl und Text gleich groß; die Zahl hebt sich nur durch Fettdruck ab.
const FUNNEL_FONT_SIZE = '0.7rem'

// Zeilen mit Erklärung sind unterpunktet; die Erklärung erscheint beim Hovern.
const sxFunnelHint = {
	cursor: 'help',
	textDecoration: 'underline dotted',
	textUnderlineOffset: '0.2em',
} as const

const FunnelRow = React.memo(
	({
		value,
		text,
		tooltip,
		highlight,
	}: {
		value: string
		text: string
		tooltip?: string
		highlight?: boolean
	}) => (
		<>
			<Txt
				variant="caption"
				fontSize={FUNNEL_FONT_SIZE}
				fontWeight="bold"
				textAlign="right"
				color={highlight ? BRAND_COLOR : 'text.primary'}
			>
				{value}
			</Txt>
			{tooltip ? (
				<Tooltip title={tooltip} slotProps={slotPropsTooltip} arrow>
					<Box component="span" sx={sxFunnelHint}>
						<Txt
							variant="caption"
							fontSize={FUNNEL_FONT_SIZE}
							color={highlight ? BRAND_COLOR : 'text.secondary'}
						>
							{text}
						</Txt>
					</Box>
				</Tooltip>
			) : (
				<Txt
					variant="caption"
					fontSize={FUNNEL_FONT_SIZE}
					color={highlight ? BRAND_COLOR : 'text.secondary'}
				>
					{text}
				</Txt>
			)}
		</>
	)
)
FunnelRow.displayName = 'FunnelRow'

// Der Ortsname; beim Hovern Einwohner im Liefergebiet und das Potenzial des Orts.
const SettlementName = React.memo(({ settlement }: { settlement: Settlement }): JSX.Element => {
	const tooltip = React.useMemo(
		() =>
			`${formatNumber(
				settlement.inhabitants5min
			)} Einwohner in ${DELIVERY_AREA_MINUTES} Minuten Fahrzeit um das Ortszentrum` +
			(settlement.potential
				? `\nPotenzial: ${formatEuro(
						settlement.potential.monthlyPlatformRevenue
				  )} für Mitbestellt im Monat · ${formatNumber(
						settlement.potential.monthlyGroupOrders
				  )} Sammelbestellungen`
				: ''),
		[settlement]
	)

	return (
		<Tooltip title={tooltip} slotProps={slotPropsTooltip} arrow>
			<Box sx={sxMetric}>
				<Txt variant="h6">{settlement.name}</Txt>
				{settlement.inhabitants5min === null ? null : (
					<Txt variant="caption" color="text.secondary">
						{formatNumber(settlement.inhabitants5min)} Einwohner in {DELIVERY_AREA_MINUTES} min
					</Txt>
				)}
			</Box>
		</Tooltip>
	)
})
SettlementName.displayName = 'SettlementName'

const formatEuroTick = (value: number): string => `${value} €`

const formatEuroCents = (value: number): string =>
	`${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`

// Euro-Beschriftungen brauchen an der Achse etwas mehr Platz als Einwohnerzahlen.
const POTENTIAL_PLOT_LEFT = 38

// Mitbestellt-Umsatz je Haushaltsgröße als Balken; ein Klick zeigt darunter, woraus er entsteht:
// erreichbare Restaurants allein und per Sammelbestellung, unversorgt, davon behebbar, Besteller.
const NO_PROVIDERS: SettlementRestaurant[] = []

const DOT_PLOT_WIDTH = 110
const DOT_PLOT_LEFT = 32
const DOT_RADIUS = 3.5
// Abstand der Punkte mit gleichem Mindestbestellwert; wird enger, wenn sie sonst nicht passen.
const DOT_SPACING = 9

const sxChartRow = { display: 'flex', alignItems: 'flex-start', gap: '0.75rem' } as const

// Beide Diagrammtitel gleich hoch wie das Info-Symbol, damit die Gitterlinien auf einer Höhe liegen.
const sxCaptionWithIcon = {
	display: 'flex',
	alignItems: 'center',
	gap: '0.25rem',
	height: '1.5rem',
} as const

// Jeder liefernde Anbieter als Punkt auf der Höhe seines Mindestbestellwerts; gleiche Werte
// stehen nebeneinander. Anbieter ohne Angabe stehen bei 0 €.
const MinOrderDotPlot = React.memo(
	({ providers }: { providers: SettlementRestaurant[] }): JSX.Element => {
		const plot = React.useMemo(() => {
			const maxValue = Math.max(0, ...providers.map(({ minOrderValue }) => minOrderValue ?? 0))
			const tickStep = getTickStep(maxValue)
			const tickMax = tickStep * Math.max(1, Math.ceil(maxValue / tickStep))
			const getY = (value: number) => PLOT_BOTTOM - ((PLOT_BOTTOM - PLOT_TOP) * value) / tickMax
			const plotWidth = DOT_PLOT_WIDTH - DOT_PLOT_LEFT
			const groups = Object.values(
				providers.reduce<Record<number, SettlementRestaurant[]>>(
					(byValue, provider) => ({
						...byValue,
						[provider.minOrderValue ?? 0]: [
							...(byValue[provider.minOrderValue ?? 0] ?? []),
							provider,
						],
					}),
					{}
				)
			)
			return {
				ticks: Array.from({ length: tickMax / tickStep + 1 }, (_, index) => ({
					value: index * tickStep,
					y: getY(index * tickStep),
				})),
				dots: groups.flatMap((group) => {
					const spacing = Math.min(DOT_SPACING, (plotWidth - 2 * DOT_RADIUS) / group.length)
					return group.map((provider, index) => ({
						provider,
						x: DOT_PLOT_LEFT + plotWidth / 2 + (index - (group.length - 1) / 2) * spacing,
						y: getY(provider.minOrderValue ?? 0),
					}))
				}),
			}
		}, [providers])

		return (
			<Box
				component="svg"
				sx={sxChart}
				width={DOT_PLOT_WIDTH}
				height={CHART_HEIGHT}
				viewBox={`0 0 ${DOT_PLOT_WIDTH} ${CHART_HEIGHT}`}
				role="img"
				aria-label="Mindestbestellwert je Anbieter"
			>
				{plot.ticks.map((tick) => (
					<g key={tick.value}>
						<line
							x1={DOT_PLOT_LEFT}
							y1={tick.y}
							x2={DOT_PLOT_WIDTH}
							y2={tick.y}
							stroke={CHART_AXIS_COLOR}
							strokeWidth={1}
						/>
						<text
							x={DOT_PLOT_LEFT - 5}
							y={tick.y + 3.5}
							textAnchor="end"
							fontSize={CHART_FONT_SIZE}
							fill={CHART_TEXT_COLOR}
						>
							{tick.value} €
						</text>
					</g>
				))}
				{plot.dots.map(({ provider, x, y }) => (
					<circle
						key={provider.id}
						cx={x}
						cy={y}
						r={DOT_RADIUS}
						fill={CHART_BAR_COLOR}
						stroke="white"
						strokeWidth={1}
					>
						<title>
							{`${provider.name}: ${
								provider.minOrderValue === null ? 'ohne MBW' : formatEuro(provider.minOrderValue)
							}${provider.driveDistanceKm === null ? '' : ` · ${provider.driveDistanceKm} km`}`}
						</title>
					</circle>
				))}
			</Box>
		)
	}
)
MinOrderDotPlot.displayName = 'MinOrderDotPlot'

const PotentialSizeChart = React.memo(
	({
		potential,
		orderRate,
		providers,
	}: {
		potential: NonNullable<Settlement['potential']>
		orderRate: number
		providers: SettlementRestaurant[]
	}): JSX.Element => {
		const sizes = React.useMemo(
			() =>
				potential.bySize.map((size, index) => ({
					...size,
					label: index === potential.bySize.length - 1 ? `${size.persons}+` : String(size.persons),
				})),
			[potential.bySize]
		)

		const chart = React.useMemo(
			() =>
				buildChart(
					sizes.map((size) => ({
						label: size.label,
						count: size.monthlyPlatformRevenue,
						tooltip: `${size.label} Pers.: ${formatEuro(size.monthlyPlatformRevenue)} im Monat`,
					})),
					POTENTIAL_PLOT_LEFT
				),
			[sizes]
		)

		const [selectedLabel, setSelectedLabel] = React.useState<string | undefined>(undefined)
		const selectedSize = sizes.find((size) => size.label === selectedLabel)

		// Ein zweiter Klick auf denselben Balken blendet die Rechnung wieder aus.
		const handleSelectSize = React.useCallback((label: string) => {
			setSelectedLabel((previous) => (previous === label ? undefined : label))
		}, [])

		// Die Annahmen hinter den Zeilen, als Tooltip an der jeweiligen Zeile.
		const explanations = React.useMemo(
			() => ({
				pricedOut:
					`Eine Person bestellt im Schnitt für ${formatEuro(
						potential.orderValuePerPerson
					)} und stockt höchstens auf 20 € auf. Wer damit kein Restaurant erreicht, ist ` +
					`unversorgt; jedes erreichbare Restaurant versorgt 1/${potential.fullSupplyRestaurants}.`,
				fixable:
					'Bis zu 3 Haushalte jeder Größe bestellen zusammen: das eigene Budget plus zwei typische ' +
					'Teilbestellungen à 40 €. So werden auch teurere Restaurants erreichbar. ' +
					'Das zählt nur bei Restaurants von außerhalb: Liegt eines schon im Ort, hilft ' +
					'gemeinsames Bestellen dort nicht.',
			}),
			[potential]
		)

		return (
			<Box m="0.75rem 0 0 0">
				<Box sx={sxChartRow}>
					<Box>
						<Box sx={sxCaptionWithIcon}>
							<Txt variant="caption" color="text.secondary">
								Mitbestellt/Monat je Haushaltsgröße
							</Txt>
						</Box>
						<BarChart
							chart={chart}
							selectedLabel={selectedLabel}
							onSelect={handleSelectSize}
							ariaLabel="Mitbestellt-Umsatz je Haushaltsgröße"
							formatTick={formatEuroTick}
						/>
					</Box>
					<Box>
						<Box sx={sxCaptionWithIcon}>
							<Txt variant="caption" color="text.secondary">
								MBW je Anbieter
							</Txt>
						</Box>
						<MinOrderDotPlot providers={providers} />
					</Box>
				</Box>
				{selectedSize ? (
					<Box sx={sxSelection}>
						<Txt variant="body2" fontWeight="bold">
							{selectedSize.label} Pers. · Budget {formatEuro(selectedSize.budget)}
						</Txt>
						<Box sx={sxFunnel}>
							<FunnelRow
								value={formatNumber(potential.inhabitants)}
								text="Einwohner in 5 min Fahrzeit"
							/>
							<FunnelRow
								value={formatNumber(selectedSize.inhabitants)}
								text={`davon ${formatPercent(
									potential.inhabitants ? selectedSize.inhabitants / potential.inhabitants : 0
								)} in ${selectedSize.label}-Pers.-Haushalten`}
							/>
							<FunnelRow
								value={formatNumber(selectedSize.inhabitants * selectedSize.pricedOutShare)}
								text={`davon ${formatPercent(selectedSize.pricedOutShare)} unversorgt (${
									selectedSize.reachable
								} ${
									selectedSize.reachable === 1 ? 'Restaurant' : 'Restaurants'
								} allein erreichbar)`}
								tooltip={explanations.pricedOut}
							/>
							<FunnelRow
								value={formatNumber(selectedSize.inhabitants * selectedSize.fixableShare)}
								text={`davon ${formatPercent(
									selectedSize.pricedOutShare
										? selectedSize.fixableShare / selectedSize.pricedOutShare
										: 0
								)} behebbar (+${selectedSize.groupReachable} per Sammelbestellung bis ${formatEuro(
									selectedSize.groupBudget
								)})`}
								tooltip={explanations.fixable}
							/>
							<FunnelRow
								value={formatNumber(selectedSize.monthlyCustomers)}
								text={`davon ${formatPercent(
									orderRate
								)} Besteller (Bestellquote), in ${formatNumber(
									selectedSize.monthlyHouseholds
								)} Haushalten`}
							/>
							<FunnelRow
								value={formatEuro(selectedSize.monthlyLostRevenue)}
								text={`liegender Umsatz/Monat (× ${formatEuro(
									potential.orderValuePerPerson
								)} Bestellwert/Person)`}
							/>
							<FunnelRow
								value={formatEuro(selectedSize.monthlyServiceFeeRevenue)}
								text={`${formatPercent(
									potential.serviceFeeShare
								)} Servicegebühr von den Bestellern`}
							/>
							<FunnelRow
								value={formatEuro(selectedSize.monthlyRestaurantFeeRevenue)}
								text={`+ ${formatNumber(
									selectedSize.monthlyGroupOrders
								)} Sammelbest. × ${formatEuroCents(
									potential.restaurantFeePerGroupOrder
								)} von Restaurants`}
							/>
							<FunnelRow
								value={formatEuro(selectedSize.monthlyPlatformRevenue)}
								text="für Mitbestellt/Monat"
								highlight
							/>
						</Box>
					</Box>
				) : null}
				{providers.length ? <ProviderDetails providers={providers} /> : null}
			</Box>
		)
	}
)
PotentialSizeChart.displayName = 'PotentialSizeChart'

// Rechte Spalte mit Gemeinde- und Restaurant-Infokarte untereinander, je nach Ansicht oben die eine.
const sxDetailColumn = {
	position: 'absolute',
	right: '1rem',
	top: '1rem',
	zIndex: 1000,
	width: '24rem',
	maxHeight: 'calc(100% - 2rem)',
	overflowY: 'auto',
	display: 'flex',
	flexDirection: 'column',
	gap: '0.75rem',
	p: '0 0.25rem 0.25rem 0.25rem',
} as const

// Die Gemeinde als Karte in der rechten Spalte.
const sxSettlementCard = {
	flexShrink: 0,
	bgcolor: 'white',
	boxShadow: 2,
	borderRadius: '0.5rem',
	p: '1rem',
} as const

// Links neben der Restaurant-Infokarte: Tourrechnung und Gesprächsleitfaden untereinander,
// gemeinsam scrollbar. Die Breite gibt die Tabelle vor, mit 4er-Spalte wird sie breiter.
const sxTourColumn = {
	position: 'absolute',
	right: '26.5rem',
	top: '1rem',
	zIndex: 1000,
	width: 'auto',
	maxWidth: 'calc(100% - 50rem)',
	maxHeight: 'calc(100% - 2rem)',
	overflowY: 'auto',
	display: 'flex',
	flexDirection: 'column',
	gap: '0.75rem',
	p: '0 0.25rem 0.25rem 0.25rem',
} as const

const sxTourCard = {
	position: 'relative',
	flexShrink: 0,
	bgcolor: 'white',
	boxShadow: 2,
	borderRadius: '0.5rem',
	p: '1rem',
} as const

// Kopf der Tourkarte: links der Link zum Rechner, rechts Schließen.
const sxTourCardHeader = {
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	m: '-0.5rem -0.5rem 0 -0.5rem',
} as const

// Kopiert den Link auf den Landing-Rechner mit Strecke, MBW und Liefergebühr dieser Tour, für Mails.
const CalculatorLinkButton = React.memo(({ target }: { target: TourTarget }): JSX.Element => {
	const { tour } = useRestaurantTour(target, true)
	const [copied, setCopied] = React.useState(false)

	const handleCopy = React.useCallback(async () => {
		const singleTour = tour?.tours.find(({ isSingleOrder }) => isSingleOrder)
		// Koordinaten statt Ortsnamen, damit der Rechner genau die Strecke dieser Tourrechnung fährt.
		const searchParams = new URLSearchParams({
			from:
				tour?.restaurantLat && tour.restaurantLon
					? `${tour.restaurantLat},${tour.restaurantLon}`
					: tour?.city ?? '',
			to: `${tour?.settlementLat},${tour?.settlementLon}`,
			mbw: String(singleTour?.orderValue ?? ''),
			df: String(singleTour?.deliveryFee ?? ''),
		})
		await navigator.clipboard.writeText(`${LANDING_URL}/restaurant?${searchParams}`)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}, [tour])

	return (
		<IconButton
			iconName={copied ? 'Check' : 'ContentCopy'}
			toolTip={copied ? 'Link kopiert' : 'Link zum Rechner kopieren'}
			onClick={handleCopy}
			disabled={!tour?.success}
		/>
	)
})
CalculatorLinkButton.displayName = 'CalculatorLinkButton'

// embedded: unter der Restaurant-Infokarte, nur mit der Anbieterliste.
const SettlementDetail = React.memo(
	({ settlement, embedded }: { settlement: Settlement; embedded?: boolean }): JSX.Element => {
		// Beim Öffnen fehlende Strecken bei Mapbox holen; die API speichert sie für alle späteren Aufrufe.
		const slugs = React.useMemo(
			() => (settlement.restaurants ?? []).map(({ slug }) => slug),
			[settlement.restaurants]
		)
		const settlementIds = React.useMemo(() => [settlement.id], [settlement.id])
		useTours(slugs, settlementIds)

		return (
			<Box sx={sxSettlementCard}>
				<Box sx={sxDetailHeader}>
					<Box minWidth="0">
						<Box sx={sxSettlementTitle}>
							<SettlementName settlement={settlement} />
							{!embedded && settlement.restaurants?.length ? (
								<SettlementLetterButton settlement={settlement} />
							) : null}
							{settlement.zipCode ? (
								<LieferandoLinkButton
									url={getLieferandoSettlementUrl({
										zipCode: settlement.zipCode,
										lat: settlement.lat,
										lon: settlement.lon,
									})}
									toolTip="Lieferando-Liste am Ortskern öffnen"
								/>
							) : null}
						</Box>
					</Box>
					{settlement.ageAvg === null ? null : (
						<AgeBadge settlement={settlement} ageAvg={settlement.ageAvg} />
					)}
				</Box>
				<Box sx={sxDivider} />
				{/* In der Restaurantansicht nur die Anbieterliste, ohne Diagramme. */}
				{embedded ? (
					settlement.restaurants?.length ? (
						<ProviderDetails providers={settlement.restaurants} />
					) : null
				) : settlement.potential && settlement.orderRate !== null ? (
					<>
						<Box sx={sxDetailHeader}>
							<Txt variant="subtitle2">Umsatzpotenzial</Txt>
							<Txt variant="subtitle2" title="Was Mitbestellt im Monat im ganzen Ort verdienen kann">
								{formatEuro(settlement.potential.monthlyPlatformRevenue)} / Monat
							</Txt>
						</Box>
						<PotentialSizeChart
							key={settlement.id}
							potential={settlement.potential}
							orderRate={settlement.orderRate}
							providers={settlement.restaurants ?? NO_PROVIDERS}
						/>
					</>
				) : null}
				{settlement.restaurants ? (
					settlement.restaurants.length ? null : (
						<Txt variant="body2" color="text.secondary">
							Geprüft — es liefert niemand hierher.
						</Txt>
					)
				) : (
					<ImportDelivery key={settlement.id} settlementId={settlement.id} />
				)}
				{/* Erst nach der Erhebung: nur dann ist bekannt, wer schon liefert. */}
				{!embedded && settlement.restaurants ? (
					<SettlementCandidates key={settlement.id} settlementId={settlement.id} />
				) : null}
			</Box>
		)
	}
)
SettlementDetail.displayName = 'SettlementDetail'

type ViewTab = 'settlements' | 'restaurants' | 'ideas'

const IdeaRow = React.memo(
	({
		idea,
		marginShare,
		monthlyPlatformRevenue,
		selected,
		onSelect,
	}: {
		idea: RestaurantSettlementIdea
		selected: boolean
		// Beide null, solange das Restaurant noch nicht in die Gemeinde liefert.
		marginShare: number | null
		monthlyPlatformRevenue: number | null
		onSelect: (idea: RestaurantSettlementIdea) => void
	}): JSX.Element => {
		const handleClick = React.useCallback(() => onSelect(idea), [idea, onSelect])

		return (
			<Box sx={selected ? sxRowSelected : sxRow} onClick={handleClick}>
				<Box flex="1" minWidth="0">
					<Txt variant="body2" noWrap title={idea.restaurantName}>
						{idea.restaurantName}
					</Txt>
					<Txt variant="caption" color="text.secondary" noWrap display="block">
						{idea.settlementName}
						{monthlyPlatformRevenue === null ? ' · liefert noch nicht' : ''}
					</Txt>
				</Box>
				<Txt
					variant="caption"
					color="text.secondary"
					width="3rem"
					textAlign="right"
					title={MARGIN_TOOLTIP}
				>
					{formatPercentOrDash(marginShare)}
				</Txt>
				<Txt variant="body2" width={POTENTIAL_COLUMN_WIDTH} textAlign="right">
					{formatEuro(monthlyPlatformRevenue)}
				</Txt>
			</Box>
		)
	}
)
IdeaRow.displayName = 'IdeaRow'

// Gemerkte Kombinationen aus Restaurant und Gemeinde, nach dem Mitbestellt-Potenzial des
// Restaurants dort; noch nicht liefernde stehen am Ende.
const IdeaList = React.memo(
	({
		ideas,
		settlements,
		excludeSingles,
		selectedIdeaId,
		onSelect,
	}: {
		ideas: RestaurantSettlementIdea[]
		settlements: Settlement[]
		excludeSingles: boolean
		// "slug:settlementId" der Strecke auf der Karte
		selectedIdeaId: string | undefined
		onSelect: (idea: RestaurantSettlementIdea) => void
	}): JSX.Element => {
		const rows = React.useMemo(
			() =>
				ideas
					.map((idea) => {
						const provider = settlements
							.find(({ id }) => id === idea.settlementId)
							?.restaurants?.find(({ slug }) => slug === idea.slug)
						return {
							idea,
							marginShare: provider ? provider.singleOrderMarginShare : null,
							// Auf volle Euro wie in der Restaurantliste.
							monthlyPlatformRevenue: provider
								? Math.round(
										provider.potentialBySize
											.filter(({ persons }) => !excludeSingles || persons !== 1)
											.reduce((sum, size) => sum + size.monthlyPlatformRevenue, 0)
								  )
								: null,
						}
					})
					.sort(
						(a, b) => (b.monthlyPlatformRevenue ?? -1) - (a.monthlyPlatformRevenue ?? -1)
					),
			[excludeSingles, ideas, settlements]
		)

		return (
			<>
				<Box sx={sxHeaderRow}>
					<Txt variant="caption" color="text.secondary" flex="1">
						Restaurant · Gemeinde
					</Txt>
					<Txt variant="caption" color="text.secondary" width="3rem" textAlign="right">
						DB
					</Txt>
					<Txt
						variant="caption"
						color="text.secondary"
						width={POTENTIAL_COLUMN_WIDTH}
						textAlign="right"
						title="Was Mitbestellt im Monat mit dem Restaurant in dieser Gemeinde verdient"
					>
						Potenzial ↓
					</Txt>
				</Box>
				{rows.length ? (
					rows.map((row) => (
					<IdeaRow
						key={row.idea.id}
						{...row}
						selected={row.idea.id === selectedIdeaId}
						onSelect={onSelect}
					/>
				))
				) : (
					<Txt variant="caption" color="text.secondary" m="0.5rem 0 0 0">
						Noch keine Kombis. Auf einer Strecke merkt die Glühbirne die Kombination.
					</Txt>
				)}
			</>
		)
	}
)
IdeaList.displayName = 'IdeaList'

// Der Restaurantpunkt auf der Karte; ein Klick darauf öffnet keinen Ort.
const RESTAURANT_FEATURE_PREFIX = 'restaurant:'
const RESTAURANT_MARKER_COLOR = '#231d1a'

const sxTabs = { m: '-0.5rem 0 0.5rem 0', minHeight: '2.5rem' } as const

const SettlementMapView = (): JSX.Element => {
	const { data: currentUser } = useCurrentUser()
	// Aus der Restaurantansicht kommt dessen Slug als Router-State; das Restaurant ist dann ausgewählt.
	const initialSlug = useLocation().state as string | null
	const windowDimensions = useWindowDimensions()
	const pxPerRem = usePxPerRemFactor()
	const mapHeight = windowDimensions.height - APP_BAR_HEIGHT_REM * pxPerRem

	const [hideWithoutProviders, setHideWithoutProviders] = React.useState(true)
	const [selectedId, setSelectedId] = React.useState<string | undefined>(undefined)
	const [selectedSlug, setSelectedSlug] = React.useState<string | undefined>(
		initialSlug ?? undefined
	)
	// Eine Gemeinde, die aus der Restaurantansicht geöffnet wurde; das Restaurant bleibt ausgewählt.
	const [restaurantSettlementId, setRestaurantSettlementId] = React.useState<string | undefined>(
		undefined
	)
	const [tab, setTab] = React.useState<ViewTab>('restaurants')
	// Das angeklickte Restaurant, dessen Strecke in die Gemeinde auf der Karte steht: Anbieter,
	// Kandidat oder Idee. Gilt nur, solange diese Gemeinde ausgewählt ist.
	const [routeSelection, setRouteSelection] = React.useState<
		{ settlementId: string; slug: string } | undefined
	>(undefined)

	const [excludeSingles, setExcludeSingles] = React.useState(true)
	const [excludeChains, setExcludeChains] = React.useState(true)
	// Das Leeren der Auswahl ignoriert der Handler.
	const [fullSupplyRestaurants, setFullSupplyRestaurants] = React.useState(3)
	const handleChangeFullSupplyRestaurants = React.useCallback((value: string | null) => {
		if (value) setFullSupplyRestaurants(Number(value))
	}, [])

	const [state, setState] = React.useState(STATES[0])
	const handleChangeState = React.useCallback((value: string | null) => {
		const nextState = STATES.find((item) => item.value === value)
		if (nextState) {
			setState(nextState)
			setSelectedId(undefined)
			setSelectedSlug(undefined)
			setRestaurantSettlementId(undefined)
			setRouteSelection(undefined)
		}
	}, [])

	const { settlements: loadedSettlements } = useSettlements(state.value, fullSupplyRestaurants)

	// Restaurants aus allen erhobenen Orten, nicht nur den gelisteten: Für ein Restaurant zählt
	// jeder Ort, in den es liefert.
	const rankedRestaurants = React.useMemo(
		() => getRankedRestaurants(loadedSettlements, excludeSingles),
		[excludeSingles, loadedSettlements]
	)

	// Ketten fallen nur aus der Rangliste; Detailkarten anderer Tabs finden sie weiter samt Ortsliste.
	const listedRestaurants = React.useMemo(
		() =>
			excludeChains ? rankedRestaurants.filter(({ isChain }) => !isChain) : rankedRestaurants,
		[excludeChains, rankedRestaurants]
	)

	// Ohne Potenzial fehlt ein Restaurant in der Rangliste; dann reicht seine Anbieterzeile aus
	// irgendeiner Gemeinde, ohne Ortsliste.
	const findRestaurant = React.useCallback(
		(slug: string | undefined): RankedRestaurant | undefined => {
			const ranked = slug ? rankedRestaurants.find((restaurant) => restaurant.slug === slug) : undefined
			const provider =
				slug && !ranked
					? loadedSettlements
							.flatMap(({ restaurants }) => restaurants ?? [])
							.find((restaurant) => restaurant.slug === slug)
					: undefined
			return (
				ranked ??
				(provider && {
					slug: provider.slug,
					name: provider.name,
					addressLine: provider.addressLine,
					addressZipCode: provider.addressZipCode,
					addressCity: provider.addressCity,
					cuisines: provider.cuisines,
					phone: provider.phone,
					email: provider.email,
					relevanceScore: provider.relevanceScore,
					isChain: provider.isChain,
					rating: provider.rating,
					ratingCount: provider.ratingCount,
					lat: provider.lat,
					lon: provider.lon,
					monthlyRevenue: 0,
					monthlyPlatformRevenue: 0,
					singleOrderMarginShare: null,
					unprofitableEverywhere: false,
					settlements: [],
				})
			)
		},
		[loadedSettlements, rankedRestaurants]
	)

	const selectedRestaurant = React.useMemo(
		() => findRestaurant(selectedSlug),
		[findRestaurant, selectedSlug]
	)

	// Ohne Single-Haushalte: deren Anteil fällt aus dem Potenzial jedes Orts heraus, damit Liste,
	// Karte und Detailkarte dasselbe zeigen.
	const settlements = React.useMemo(
		() =>
			excludeSingles
				? loadedSettlements.map((settlement) =>
						settlement.potential
							? { ...settlement, potential: withoutSingles(settlement.potential) }
							: settlement
				  )
				: loadedSettlements,
		[excludeSingles, loadedSettlements]
	)

	// Sortiert nach Potenzial. Orte ohne Liefererhebung haben keins und rutschen damit ans Ende,
	// statt aus der Liste zu fallen.
	const rankedSettlements = React.useMemo(
		() =>
			settlements
				.filter((settlement) => (settlement.inhabitants5min ?? 0) >= MIN_INHABITANTS)
				.sort((a, b) => (getPotential(b) ?? -1) - (getPotential(a) ?? -1)),
		[settlements]
	)

	// Blendet auch nicht erhobene Orte aus; der Sammelabruf bekommt deshalb rankedSettlements.
	const visibleSettlements = React.useMemo(
		() =>
			hideWithoutProviders
				? rankedSettlements.filter((settlement) => settlement.restaurants?.length)
				: rankedSettlements,
		[hideWithoutProviders, rankedSettlements]
	)

	const maxPotential = React.useMemo(
		() => Math.max(0, ...visibleSettlements.map((settlement) => getPotential(settlement) ?? 0)),
		[visibleSettlements]
	)

	const potentialTotalsText = React.useMemo(
		() => getPotentialTotalsText(visibleSettlements),
		[visibleSettlements]
	)

	// Mit ausgewähltem Restaurant nur dessen Orte, eingefärbt nach seinem Umsatz dort, und das
	// Restaurant selbst als dunkler Punkt mit weißem Rand.
	const features = React.useMemo(() => {
		if (tab === 'restaurants' && selectedRestaurant) {
			const maxRevenue = Math.max(
				0,
				...selectedRestaurant.settlements.map(({ monthlyRevenue }) => monthlyRevenue)
			)
			return [
				...selectedRestaurant.settlements.flatMap((potential) => {
					const settlement = settlements.find(({ id }) => id === potential.settlementId)
					return settlement
						? [
								{
									id: settlement.id,
									lat: settlement.lat,
									lon: settlement.lon,
									color: getPotentialColor(potential.monthlyRevenue, maxRevenue),
									pointSize: getPointSize(potential.monthlyRevenue, maxRevenue),
								},
						  ]
						: []
				}),
				...(selectedRestaurant.lat !== null && selectedRestaurant.lon !== null
					? [
							{
								id: `${RESTAURANT_FEATURE_PREFIX}${selectedRestaurant.slug}`,
								lat: selectedRestaurant.lat,
								lon: selectedRestaurant.lon,
								color: RESTAURANT_MARKER_COLOR,
								pointSize: 7,
								strokeWidth: 2,
							},
					  ]
					: []),
			]
		} else {
			return visibleSettlements.map((settlement) => ({
				id: settlement.id,
				lat: settlement.lat,
				lon: settlement.lon,
				color: getPotentialColor(getPotential(settlement), maxPotential),
				pointSize: getPointSize(getPotential(settlement), maxPotential),
			}))
		}
	}, [maxPotential, selectedRestaurant, settlements, tab, visibleSettlements])

	const selectedSettlement = React.useMemo(
		() => settlements.find((settlement) => settlement.id === selectedId),
		[selectedId, settlements]
	)

	// Im Ideen-Tab richtet sich der Ausschnitt nach der Strecke (bounds), nicht nach der Gemeinde.
	const focus = React.useMemo(
		() =>
			tab === 'settlements' && selectedSettlement
				? { lat: selectedSettlement.lat, lon: selectedSettlement.lon, zoom: 10 }
				: undefined,
		[selectedSettlement, tab]
	)

	const handleSelect = React.useCallback((id: string) => setSelectedId(id), [])
	const handleSelectRestaurant = React.useCallback((slug: string) => {
		setSelectedSlug(slug)
		setRestaurantSettlementId(undefined)
	}, [])
	// Ein zweiter Klick auf dieselbe Gemeinde blendet ihre Details wieder aus.
	const handleSelectRestaurantSettlement = React.useCallback(
		(settlementId: string) =>
			setRestaurantSettlementId((previous) =>
				previous === settlementId ? undefined : settlementId
			),
		[]
	)

	// Die Gemeinde, deren Anbieterliste gerade offen ist: in Siedlungs- und Ideenansicht die
	// ausgewählte, in der Restaurantansicht die im Restaurant angeklickte.
	const activeSettlementId = tab !== 'restaurants' ? selectedId : restaurantSettlementId
	const restaurantSettlement = React.useMemo(
		() =>
			tab === 'restaurants'
				? settlements.find(({ id }) => id === restaurantSettlementId)
				: undefined,
		[restaurantSettlementId, settlements, tab]
	)

	// Welche Strecke auf der Karte steht: das angeklickte Restaurant dieser Gemeinde, sonst in der
	// Restaurantansicht das ausgewählte Restaurant selbst, wenn es in die angeklickte Gemeinde liefert.
	const routeSlug =
		(routeSelection?.settlementId === activeSettlementId ? routeSelection?.slug : undefined) ??
		(tab === 'restaurants' &&
		selectedRestaurant?.settlements.some(
			({ settlementId }) => settlementId === restaurantSettlementId
		)
			? selectedRestaurant.slug
			: undefined)

	// In Siedlungs- und Kombi-Ansicht steht das Restaurant der Strecke als Infokarte unter der Gemeinde,
	// auch wenn es dort noch nicht liefert.
	const settlementRestaurant = React.useMemo(
		() => (tab !== 'restaurants' ? findRestaurant(routeSlug) : undefined),
		[findRestaurant, routeSlug, tab]
	)

	// Ein zweiter Klick auf dasselbe Restaurant blendet seine Strecke wieder aus; Anbieter und
	// Kandidaten teilen sich die Auswahl, damit nur eine Strecke auf der Karte steht.
	const handleSelectRouteSlug = React.useCallback(
		(slug: string) => {
			// In der Restaurantansicht wird das angeklickte Restaurant oben zur Infokarte, die Gemeinde bleibt offen.
			if (tab === 'restaurants') setSelectedSlug(slug)
			setRouteSelection((previous) =>
				activeSettlementId === undefined ||
				(previous?.settlementId === activeSettlementId && previous.slug === slug)
					? undefined
					: { settlementId: activeSettlementId, slug }
			)
		},
		[activeSettlementId, tab]
	)

	const tourSelection = React.useMemo(
		() => ({ selectedSlug: routeSlug, onSelectSlug: handleSelectRouteSlug }),
		[handleSelectRouteSlug, routeSlug]
	)

	// Wofür Strecke und Tourrechnung gelten; der Schlüssel im Format der Ideen-id ("slug:settlementId").
	const routeTarget = React.useMemo(
		(): TourTarget | undefined =>
			routeSlug && activeSettlementId
				? { slug: routeSlug, settlementId: activeSettlementId }
				: undefined,
		[activeSettlementId, routeSlug]
	)
	const routeTargetKey = routeTarget ? `${routeTarget.slug}:${routeTarget.settlementId}` : undefined

	// Die Strecke steht auf der großen Karte; die Tourrechnung erst nach Klick auf ihren Info-Knopf.
	// Gemerkt wird das Ziel, so blendet eine andere Strecke die Rechnung von selbst aus.
	const [tourInfoKey, setTourInfoKey] = React.useState<string | undefined>(undefined)
	const { tour: routeTour } = useRestaurantTour(routeTarget)
	const handleToggleTourInfo = React.useCallback(
		() => setTourInfoKey((previous) => (previous === routeTargetKey ? undefined : routeTargetKey)),
		[routeTargetKey]
	)
	const handleCloseTourInfo = React.useCallback(() => setTourInfoKey(undefined), [])

	// Die Kombination aus Restaurant und Gemeinde der Strecke auf der Karte; die Glühbirne merkt sie.
	const { ideas } = useRestaurantSettlementIdeas()
	const routeIdeaMarked = ideas.some(({ id }) => id === routeTargetKey)
	const handleToggleRouteIdea = React.useCallback(async () => {
		const result = await setRestaurantSettlementIdea({
			...(routeTarget as TourTarget),
			marked: !routeIdeaMarked,
		})
		if (!result.success) handleMutationError('setRestaurantSettlementIdea', result.errors)
	}, [routeIdeaMarked, routeTarget])

	const mapRoute = React.useMemo(
		() =>
			routeTarget && routeTour?.routeCoordinates
				? {
						coordinates: routeTour.routeCoordinates,
						// Wie die Beschriftung im Kartenbild der Tourrechnung (getTours).
						label: `${(routeTour.distanceKm ?? 0).toLocaleString('de-DE', {
							maximumFractionDigits: 1,
						})} km · ${Math.round(routeTour.durationMinutes ?? 0)} min`,
						onClickInfo: handleToggleTourInfo,
						marked: routeIdeaMarked,
						onToggleMark: handleToggleRouteIdea,
				  }
				: undefined,
		[
			handleToggleRouteIdea,
			handleToggleTourInfo,
			routeIdeaMarked,
			routeTarget,
			routeTour?.distanceKm,
			routeTour?.durationMinutes,
			routeTour?.routeCoordinates,
		]
	)

	// Die Ideen des gewählten Bundeslands; ein Klick öffnet im Ideen-Tab die Gemeinde mit der Strecke,
	// ob das Restaurant schon dorthin liefert oder nicht.
	const stateIdeas = React.useMemo(
		() => ideas.filter((idea) => idea.state === state.value),
		[ideas, state.value]
	)
	const handleSelectIdea = React.useCallback((idea: RestaurantSettlementIdea) => {
		setSelectedId(idea.settlementId)
		setRouteSelection({ settlementId: idea.settlementId, slug: idea.slug })
	}, [])

	const handleChangeTab = React.useCallback(
		(_event: React.SyntheticEvent, value: ViewTab) => setTab(value),
		[]
	)

	// Mit ausgewähltem Restaurant öffnet ein Klick die Gemeinde neben dem Restaurant, sonst wechselt
	// er in die Siedlungsansicht. Der Restaurantpunkt selbst öffnet nichts.
	const handleClickMapFeature = React.useCallback(
		(id: string) => {
			if (id.startsWith(RESTAURANT_FEATURE_PREFIX)) return
			if (tab === 'restaurants' && selectedSlug) {
				setRestaurantSettlementId(id)
			} else {
				setTab('settlements')
				setSelectedId(id)
			}
		},
		[selectedSlug, tab]
	)

	// Der Ausschnitt als Schlüssel: in der Restaurantansicht Restaurant und seine Orte, im Ideen-Tab die
	// Strecke. Laden Fahrzeiten nach, ändert sich das Objekt, der Ausschnitt aber nicht.
	const boundsKey = React.useMemo(() => {
		const points =
			tab === 'restaurants' && selectedRestaurant
				? [
						...(selectedRestaurant.lat !== null && selectedRestaurant.lon !== null
							? [[selectedRestaurant.lon, selectedRestaurant.lat]]
							: []),
						...selectedRestaurant.settlements.flatMap(({ settlementId }) => {
							const settlement = settlements.find(({ id }) => id === settlementId)
							return settlement ? [[settlement.lon, settlement.lat]] : []
						}),
				  ]
				: tab === 'ideas' && routeTarget
				? routeTour?.routeCoordinates ?? []
				: []
		return points.length
			? [
					Math.min(...points.map(([lon]) => lon)),
					Math.min(...points.map(([, lat]) => lat)),
					Math.max(...points.map(([lon]) => lon)),
					Math.max(...points.map(([, lat]) => lat)),
			  ].join(',')
			: ''
	}, [routeTarget, routeTour?.routeCoordinates, selectedRestaurant, settlements, tab])

	// Links die Liste, rechts die Infokarte: der Ausschnitt bleibt zwischen beiden frei.
	const bounds = React.useMemo((): MapBounds | undefined => {
		if (!boundsKey) return undefined
		const [west, south, east, north] = boundsKey.split(',').map(Number)
		return {
			southWest: [west, south],
			northEast: [east, north],
			padding: { top: 60, bottom: 60, left: 24 * pxPerRem, right: 26 * pxPerRem },
		}
	}, [boundsKey, pxPerRem])

	// Pfeil hoch/runter blättert durch die Liste des offenen Tabs. Eingabefelder und offene Selects
	// behalten ihre eigene Tastensteuerung, sonst liesse sich dort nicht mehr per Tastatur waehlen.
	React.useEffect(() => {
		if (tab === 'ideas') return
		const ids =
			tab === 'settlements'
				? visibleSettlements.map(({ id }) => id)
				: listedRestaurants.map(({ slug }) => slug)
		const currentId = tab === 'settlements' ? selectedId : selectedSlug
		const select = tab === 'settlements' ? setSelectedId : setSelectedSlug
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
			const target = event.target as HTMLElement | null
			if (target?.closest?.('input, textarea, [role="listbox"], [role="combobox"]')) return
			if (!ids.length) return
			event.preventDefault()
			const currentIndex = ids.indexOf(currentId ?? '')
			const nextIndex =
				event.key === 'ArrowDown'
					? Math.min(currentIndex + 1, ids.length - 1)
					: Math.max(currentIndex - 1, 0)
			select(ids[nextIndex])
		}
		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [listedRestaurants, selectedSlug, selectedId, tab, visibleSettlements])

	const mapOverlay = React.useMemo(
		() => (
			<>
				<Box sx={sxPanel}>
					<SelectField
						label="Bundesland"
						items={STATES}
						defaultValue={state.value}
						onChange={handleChangeState}
						fullWidth
						m="0 0 1rem 0"
					/>
					<Tabs value={tab} onChange={handleChangeTab} variant="fullWidth" sx={sxTabs}>
						<Tab value="settlements" label="Siedlungen" />
						<Tab value="restaurants" label="Restaurants" />
						<Tab value="ideas" label="Kombi" />
					</Tabs>
					{tab === 'settlements' ? (
						<CheckBox
							label="Orte ohne Lieferando-Restaurants ausblenden"
							defaultValue={hideWithoutProviders}
							onChange={setHideWithoutProviders}
							small
						/>
					) : tab === 'restaurants' ? (
						<CheckBox
							label="Ketten ignorieren"
							defaultValue={excludeChains}
							onChange={setExcludeChains}
							small
						/>
					) : null}
					<CheckBox
						label="Single-Haushalte ignorieren"
						defaultValue={excludeSingles}
						onChange={setExcludeSingles}
						small
					/>
					<SelectField
						label="Voll versorgt ab Restaurants"
						items={FULL_SUPPLY_RESTAURANT_ITEMS}
						defaultValue={String(fullSupplyRestaurants)}
						onChange={handleChangeFullSupplyRestaurants}
						toolTip="So viele Restaurants, deren MBW eine Haushaltsgröße erreicht, versorgen sie voll; jedes deckt 1/n ihres Bedarfs."
						fullWidth
						m="0.5rem 0 0.75rem 0"
					/>
					{tab === 'settlements' ? (
						<>
							<BulkImportRestaurants settlements={rankedSettlements} />
							<Box sx={sxHeaderRow}>
								<Box sx={sxDot} />
								<Txt variant="caption" color="text.secondary" flex="1">
									Ort
								</Txt>
								<Box width="0.75rem" />
								<Tooltip title={potentialTotalsText} slotProps={slotPropsTooltip} arrow>
									<Box sx={sxMetric}>
										<Txt
											variant="caption"
											fontWeight={700}
											width={POTENTIAL_COLUMN_WIDTH}
											textAlign="right"
											noWrap
										>
											Potenzial ↓
										</Txt>
									</Box>
								</Tooltip>
							</Box>
							{visibleSettlements.map((settlement) => (
								<SettlementRow
									key={settlement.id}
									settlement={settlement}
									maxPotential={maxPotential}
									selected={settlement.id === selectedId}
									onSelect={handleSelect}
								/>
							))}
						</>
					) : tab === 'restaurants' ? (
						<RestaurantList
							restaurants={listedRestaurants}
							selectedSlug={selectedSlug}
							onSelect={handleSelectRestaurant}
						/>
					) : (
						<IdeaList
							ideas={stateIdeas}
							settlements={settlements}
							excludeSingles={excludeSingles}
							selectedIdeaId={routeTargetKey}
							onSelect={handleSelectIdea}
						/>
					)}
				</Box>
				<TourSelectionContext.Provider value={tourSelection}>
					{tab !== 'restaurants' && selectedSettlement ? (
						<Box sx={sxDetailColumn}>
							<SettlementDetail settlement={selectedSettlement} />
							{settlementRestaurant ? (
								<RestaurantDetail
									restaurant={settlementRestaurant}
									selectedSettlementId={selectedId}
									onSelectSettlement={handleSelect}
								/>
							) : null}
						</Box>
					) : null}
					{tab === 'restaurants' && selectedRestaurant ? (
						<Box sx={sxDetailColumn}>
							<RestaurantDetail
								restaurant={selectedRestaurant}
								selectedSettlementId={restaurantSettlementId}
								onSelectSettlement={handleSelectRestaurantSettlement}
							/>
							{restaurantSettlement ? (
								<SettlementDetail settlement={restaurantSettlement} embedded />
							) : null}
						</Box>
					) : null}
					{routeTarget &&
					tourInfoKey === routeTargetKey &&
					(tab !== 'restaurants' ? selectedSettlement : selectedRestaurant) ? (
						<Box sx={sxTourColumn}>
							<Box sx={sxTourCard}>
								<Box sx={sxTourCardHeader}>
									<CalculatorLinkButton target={routeTarget} />
									<IconButton
										iconName="CloseOutlined"
										toolTip="Tourrechnung schließen"
										onClick={handleCloseTourInfo}
									/>
								</Box>
								<RestaurantTourContent key={routeTargetKey} target={routeTarget} hideMap />
							</Box>
						</Box>
					) : null}
				</TourSelectionContext.Provider>
			</>
		),
		[
			handleSelectRestaurantSettlement,
			restaurantSettlementId,
			restaurantSettlement,
			handleChangeTab,
			handleSelect,
			handleSelectRestaurant,
			handleSelectIdea,
			stateIdeas,
			settlements,
			routeTarget,
			routeTargetKey,
			tourInfoKey,
			handleCloseTourInfo,
			settlementRestaurant,
			tourSelection,
			hideWithoutProviders,
			excludeChains,
			listedRestaurants,
			excludeSingles,
			fullSupplyRestaurants,
			handleChangeFullSupplyRestaurants,
			state.value,
			handleChangeState,
			maxPotential,
			potentialTotalsText,
			rankedSettlements,
			selectedSlug,
			selectedId,
			selectedRestaurant,
			selectedSettlement,
			tab,
			visibleSettlements,
		]
	)

	return currentUser?.isRoot ? (
		<MapContainer
			// Ein neues Land baut die Karte über seiner Mitte neu auf.
			key={state.value}
			features={features}
			onClickMapFeature={handleClickMapFeature}
			focus={focus}
			bounds={bounds}
			route={mapRoute}
			defaultLat={state.lat}
			defaultLon={state.lon}
			defaultZoom={7}
			width="100%"
			height={`${mapHeight}px`}
		>
			{mapOverlay}
		</MapContainer>
	) : (
		<Txt m="3rem 1rem">Diese Ansicht ist Root-Admins vorbehalten.</Txt>
	)
}

export default SettlementMapView
